import React, { useState, useEffect, useRef } from "react";
import { GET, RAW_POST } from "Common/api";
import { Button } from "Components/Base/Inputs/Button";
import { TextField } from "Components/Base/Inputs/TextField";
import { InputLabel } from "Components/Base/Inputs/InputLabel";
import { Grid } from "Components/Base/Layout/Grid";
import { Box } from "Components/Base/Layout/Box";
import { Typography } from "Components/Base/DataDisplay/Typography";
import { IconButton } from "Components/Base/Inputs/IconButton";
import Dropzone from "Components/Fragment/Dropzone";
import InputLabelDatePicker from "Components/Fragment/InputLabelDatePicker";
import PriorityDropdown from "lib/components/PriorityDropdown";
import Form, { Label } from "lib/components/Form";
import { Select } from "Components/Base/Inputs/Select";
import { MenuItem } from "Components/Base/Inputs/MenuItem";
import HoursAndMinutesTime from "Components/Fragment/HoursAndMinutesTime";
import { openExternalURL } from "lib/utils";
import PersonalGoalsDropdown from "lib/components/PersonalGoalsDropdown";
import SettingStore from "stores/settings";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { OpenInNewIcon, DeleteOutlined } from "Components/Icons";
import SnackbarAlert from "Components/Fragment/SnackbarAlert";
import { TextFieldMultiline } from "Components/Base/Inputs/TextFieldMultiline";
import ItemMetadataFields from "lib/components/ItemMetadataFields";
import {
  ACTIVITY_FIELD,
  buildSubmitData,
  certificateIdToSelectValue,
  costFromActivity,
  getDeadlineDate,
  resolveActivityCertificateId,
  sanitizeCostInput,
  shouldShowCompletedCheckbox,
} from "./addexternal.utils";
import { validateActivity } from "./addexternal.validation";
import { useExternalMetadata } from "./addexternal.hooks";

const mobileViewport = window.matchMedia("(max-width: 400px)").matches || false;

const activityFieldHelperTextProps = {
  sx: { fontSize: "14px", lineHeight: 1.43 },
};

const AddExternal = ({
  languageContext,
  activity = {},
  personalId,
  personalName,
  fromEducationHistory = false,
  allowAttachment,
  allowUsersToCompleteCourses,
  isManager,
  onCancel,
  save,
  error: propError,
}) => {
  // Refs for legacy components that have internal state
  const priorityDropdownRef = useRef(null);
  const personalGoalDropdownRef = useRef(null);

  // Consolidated form data state (1 state variable instead of 13)
  const [activityData, setActivityData] = useState({
    title: activity.title || "",
    description: activity.description || "",
    comment: activity.comments || "",
    responsible: activity.responsible || (activity.id ? "" : personalName),
    link: activity.link || "",
    linkLabel: activity.linkLabel || "",
    isCompleted: activity.isCompleted || false,
    startDate: activity.startDate || "",
    endDate: activity.endDate || "",
    expireDate: activity.dueDate || activity.deadlineDate || "",
    hours: parseInt(activity.hours, 10) || "",
    minutes: parseInt(activity.minutes, 10) || "",
    cost: costFromActivity(activity.cost),
    personalGoalId: activity.personalGoalId || activity.goalId || "",
    files: null,
    removeAttachment: false,
    id: activity.id || null,
    approvalRequested: activity.approvalRequested || false,
    certificateId: resolveActivityCertificateId(activity),
  });

  const [uiState, setUiState] = useState({
    allowedExtensions: [],
    ready: false,
    fileError: null,
    requireApprovalForCompletion: false,
    allowCertificateSelection: false,
    certificates: [],
    success: false,
    error: false,
    errorMessage: null,
  });

  const {
    itemMetadataFields,
    itemMetadataValuesById,
    itemMetadataErrorsById,
    setItemMetadataErrorsById,
    handleItemMetadataChange,
  } = useExternalMetadata({
    ready: uiState.ready,
    activityId: activity.id,
    fromEducationHistory,
  });
  const [activityFieldErrors, setActivityFieldErrors] = useState({});

  // Helper to update form data
  const updateactivityData = (updates) => {
    setActivityData((prev) => ({ ...prev, ...updates }));
  };

  // Helper to update UI state
  const updateUiState = (updates) => {
    setUiState((prev) => ({ ...prev, ...updates }));
  };

  const clearActivityFieldErrorsByKeys = (...keys) => {
    setActivityFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((k) => {
        if (next[k]) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const scrollFormErrorsToTop = () => {
    const dialogContent = document.getElementById("dialog-content");
    if (dialogContent) {
      dialogContent.scrollTop = 0;
    }
  };

  const handleRequestApproval = async () => {
    const { activityFieldErrors: fieldErrs, itemMetadataErrorsById } =
      validateActivity({
        options: {
          requireTitle: true,
          requireStartEndForApproval: true,
          requireExpireDate: !fromEducationHistory,
          checkStartBeforeExpire: true,
          checkFileExtension: true,
        },
        activityData,
        activity,
        fromEducationHistory,
        itemMetadataFields,
        itemMetadataValuesById,
        allowedExtensions: uiState.allowedExtensions,
        languageContext,
      });
    setActivityFieldErrors(fieldErrs);
    setItemMetadataErrorsById(itemMetadataErrorsById);

    const hasBlockingErrors =
      Object.keys(fieldErrs).length > 0 ||
      Object.keys(itemMetadataErrorsById).length > 0;
    if (hasBlockingErrors) {
      scrollFormErrorsToTop();
      return;
    }

    if (!window.confirm(languageContext("confirmrequestapproval"))) {
      return;
    }

    const deadlineDate = getDeadlineDate(activityData, activity);
    const data = buildSubmitData({
      fromEducationHistory,
      personalId,
      activity,
      activityData,
      itemMetadataFields,
      itemMetadataValuesById,
      deadlineDate,
      priorityValue: priorityDropdownRef.current?.state?.currentValue,
    });
    const approvalCallback = async (id) => {
      const response = await requestApproval(personalId, id);
      if (response.ok) {
        updateactivityData({ approvalRequested: true, id });
        updateUiState({ success: true });
      } else {
        updateUiState({ error: true });
      }
    };
    await save(data, activityData.files, null, approvalCallback);
  };

  const requestApproval = async (personalId, activityId) => {
    const response = await RAW_POST(
      `/v1/approval/${personalId}/personalplannedcourse/${activityId}`,
    );
    return response;
  };

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      const extensions = await GET("/v1/allowedextensions");
      const availableCertificates = await GET(
        `/v1/users/${personalId}/accessible-certificates`,
      );
      const requireApprovalForCompletion =
        SettingStore.getSettingValue(
          "requireapprovalforexternalcoursecompletion",
        ) === "1";
      const allowCertificateSelection =
        SettingStore.getSettingValue(
          "AllowExternalCourseToGrantCertificate",
        ) === "1";
      updateUiState({
        allowedExtensions: extensions,
        ready: true,
        requireApprovalForCompletion,
        allowCertificateSelection,
        certificates: allowCertificateSelection ? availableCertificates : [],
      });
    };

    initializeComponent();
  }, []);

  // Handle prop error changes
  useEffect(() => {
    if (propError) {
      updateUiState({ fileError: languageContext(propError) });
    } else {
      updateUiState({ fileError: null });
    }
  }, [propError, languageContext]);

  // Keep certificate in sync when editing an activity whose props arrive after mount or use alternate JSON keys.
  useEffect(() => {
    const resolved = resolveActivityCertificateId(activity);
    if (resolved == null) {
      return;
    }
    setActivityData((prev) => {
      if (Number(prev.certificateId) === resolved) {
        return prev;
      }
      return { ...prev, certificateId: resolved };
    });
  }, [
    activity?.id,
    activity?.Id,
    activity?.certificateId,
    activity?.certificateID,
    activity?.CertificateID,
    activity?.CertificateId,
  ]);

  const handleDrop = (droppedFiles) => {
    updateactivityData({ files: droppedFiles, removeAttachment: false });
    updateUiState({ fileError: null });
    clearActivityFieldErrorsByKeys(ACTIVITY_FIELD.FILE_EXTENSION);
  };

  const handleRemoveFile = () => {
    if (window.confirm(languageContext("confirmdeletefile"))) {
      updateactivityData({ removeAttachment: true, files: null });
      clearActivityFieldErrorsByKeys(ACTIVITY_FIELD.FILE_EXTENSION);
    }
  };

  const updatePersonalGoalId = (personalGoalId) => {
    updateactivityData({ personalGoalId });
  };

  const handleSaveActivity = async (createApprovalRequest = false) => {
    const deadlineDate = getDeadlineDate(activityData, activity);
    const { activityFieldErrors: fieldErrs, itemMetadataErrorsById } =
      validateActivity({
        options: {
          requireTitle: true,
          requireStartEndForApproval: createApprovalRequest,
          requireExpireDate: !fromEducationHistory,
          checkStartBeforeExpire: true,
          checkFileExtension: true,
        },
        activityData,
        activity,
        fromEducationHistory,
        itemMetadataFields,
        itemMetadataValuesById,
        allowedExtensions: uiState.allowedExtensions,
        languageContext,
      });
    setActivityFieldErrors(fieldErrs);
    setItemMetadataErrorsById(itemMetadataErrorsById);

    const hasBlockingErrors =
      Object.keys(fieldErrs).length > 0 ||
      Object.keys(itemMetadataErrorsById).length > 0;

    if (!hasBlockingErrors) {
      setActivityFieldErrors({});
      const data = buildSubmitData({
        fromEducationHistory,
        personalId,
        activity,
        activityData,
        itemMetadataFields,
        itemMetadataValuesById,
        deadlineDate,
        priorityValue: priorityDropdownRef.current?.state?.currentValue,
      });
      const createApprovalRequestCallback = createApprovalRequest
        ? (id) => requestApproval(personalId, id)
        : null;
      await save(data, activityData.files, null, createApprovalRequestCallback);
    } else {
      scrollFormErrorsToTop();
    }
  };

  const renderSelectedFiles = () => {
    if (activityData.files) {
      return activityData.files.map((f) => (
        <Box key={f.name} className="dropzone-selectedfiles-file">
          {f.name}
        </Box>
      ));
    }

    if (activity.attachmentId && !activityData.removeAttachment) {
      const streamType = fromEducationHistory
        ? "externalcourse"
        : "goalactivity";
      const attachmentHref = `utils/StreamFile.aspx?type=${streamType}&param=${activity.id}&attachmentid=${activity.attachmentId}&q=${Date.now()}`;
      return (
        <Box key={activity.fileName} className="dropzone-selectedfiles-file">
          <a
            href={attachmentHref}
            className="nowrap"
            target="_blank"
            rel="noreferrer noopener"
          >
            {activity.fileName}
          </a>
        </Box>
      );
    }

    return null;
  };

  if (!uiState.ready) {
    return <Box />;
  }

  const formContentStyle = mobileViewport
    ? {}
    : { maxHeight: "650px", overflow: "auto" };
  const disableForm =
    (activityData.approvalRequested && !isManager) ||
    (fromEducationHistory && uiState.requireApprovalForCompletion) ||
    (activity.approved && activityData.isCompleted);
  const saveButtonDisabled =
    disableForm && !(activity.approved && activityData.isCompleted);
  const showCompletedCheckbox = shouldShowCompletedCheckbox({
    fromEducationHistory,
    isManager,
    allowUsersToCompleteCourses,
    requireApprovalForCompletion: uiState.requireApprovalForCompletion,
    isApproved: activity.approved,
  });

  const educationHistoryCertificateTitle =
    activity.certificates?.[0]?.certificate?.title ?? "";
  const hasEducationHistoryCertificateTitle =
    String(educationHistoryCertificateTitle).trim().length > 0;
  const showCertificateSection =
    uiState.allowCertificateSelection ||
    (fromEducationHistory && hasEducationHistoryCertificateTitle);

  const titleFieldError = activityFieldErrors[ACTIVITY_FIELD.TITLE] || "";
  const expireFieldError =
    activityFieldErrors[ACTIVITY_FIELD.EXPIRE_DATE] || "";
  const startFieldError = activityFieldErrors[ACTIVITY_FIELD.START_DATE] || "";
  const endFieldError = activityFieldErrors[ACTIVITY_FIELD.END_DATE] || "";
  const dateRangeFieldError =
    activityFieldErrors[ACTIVITY_FIELD.DATE_RANGE] || "";
  const startExpireFieldError =
    activityFieldErrors[ACTIVITY_FIELD.START_EXPIRE_ORDER] || "";
  const costFieldError = activityFieldErrors[ACTIVITY_FIELD.COST] || "";
  const fileExtensionFieldError =
    activityFieldErrors[ACTIVITY_FIELD.FILE_EXTENSION] || "";

  return (
    <Form noValidate onSubmit={handleSaveActivity}>
      <Box
        className={`${!mobileViewport ? "scrollbar " : ""}add-external-inner-container`}
        sx={formContentStyle}
      >
        <Box mb={1.25}>
          <Label
            htmlFor="addactivity-title"
            className="required"
            label={`${languageContext("title")}:`}
          />
          <TextField
            id="addactivity-title"
            fullWidth
            disabled={disableForm}
            value={activityData.title}
            onChange={(e) => {
              updateactivityData({ title: e.target.value });
              clearActivityFieldErrorsByKeys(ACTIVITY_FIELD.TITLE);
            }}
            required
            error={Boolean(titleFieldError)}
            helperText={titleFieldError}
            FormHelperTextProps={{
              ...activityFieldHelperTextProps,
              ...(titleFieldError
                ? { id: "addactivity-title-helper-text" }
                : {}),
            }}
            inputProps={{
              "aria-invalid": titleFieldError ? "true" : "false",
              "aria-describedby": titleFieldError
                ? "addactivity-title-helper-text"
                : undefined,
            }}
          />
        </Box>

        <Box mb={1.25}>
          <Typography
            component="label"
            htmlFor="TxtDescription"
            variant="body1"
          >
            {languageContext("description")}:
          </Typography>
          <TextFieldMultiline
            id="TxtDescription"
            minRows={3}
            fullWidth
            disabled={disableForm}
            value={activityData.description}
            onChange={(e) =>
              updateactivityData({ description: e.target.value })
            }
          />
        </Box>

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography component="label" htmlFor="SelPrio" variant="body1">
              {languageContext("priority")}:
            </Typography>
            <PriorityDropdown
              ref={priorityDropdownRef}
              defaultValue={activity.priority}
              id="SelPrio"
              disabled={disableForm}
            />
          </Box>
        )}
        {showCertificateSection && (
          <Box mb={1.25} sx={{ width: "50%" }}>
            <Typography
              component="label"
              htmlFor="SelCertificate"
              variant="body1"
            >
              {languageContext("certificate")}:
            </Typography>
            {fromEducationHistory && hasEducationHistoryCertificateTitle ? (
              <TextField
                id="SelCertificate"
                fullWidth
                disabled
                value={educationHistoryCertificateTitle}
                inputProps={{
                  "aria-label": languageContext("certificate"),
                }}
              />
            ) : (
              <Select
                id="SelCertificate"
                fullWidth
                value={certificateIdToSelectValue(activityData.certificateId)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") {
                    updateactivityData({ certificateId: null });
                    return;
                  }
                  const n = Number(v);
                  updateactivityData({
                    certificateId: Number.isNaN(n) ? null : n,
                  });
                }}
                label={languageContext("selectcertificate")}
                displayEmpty
                inputProps={{
                  "aria-label": languageContext("selectcertificate"),
                  "aria-required": "false",
                }}
                disabled={disableForm}
              >
                <MenuItem value="">
                  - {languageContext("choosecertificate")} -
                </MenuItem>
                {uiState.certificates &&
                  uiState.certificates.map((cert) => {
                    return (
                      <MenuItem
                        key={cert.id}
                        value={certificateIdToSelectValue(cert.id)}
                      >
                        {cert.title}
                      </MenuItem>
                    );
                  })}
              </Select>
            )}
          </Box>
        )}

        <Box display="flex" sx={{ gap: 2, flexWrap: "wrap", mb: 1.25 }}>
          {!fromEducationHistory && (
            <InputLabelDatePicker
              id="CalExpireDate"
              required
              nativeRequired={false}
              label={languageContext("expiredate")}
              name={languageContext("expiredate")}
              value={activityData.expireDate}
              disabled={disableForm}
              onChange={(event) => {
                updateactivityData({ expireDate: event.target.value });
                clearActivityFieldErrorsByKeys(
                  ACTIVITY_FIELD.EXPIRE_DATE,
                  ACTIVITY_FIELD.START_EXPIRE_ORDER,
                );
              }}
              oldStyleLabel={true}
              error={Boolean(expireFieldError || startExpireFieldError)}
              helperText={expireFieldError || startExpireFieldError}
            />
          )}

          <InputLabelDatePicker
            id="CalStartDate"
            disabled={disableForm}
            label={languageContext("startdate")}
            name={languageContext("startdate")}
            value={activityData.startDate}
            onChange={(event) => {
              updateactivityData({ startDate: event.target.value });
              clearActivityFieldErrorsByKeys(
                ACTIVITY_FIELD.START_DATE,
                ACTIVITY_FIELD.DATE_RANGE,
                ACTIVITY_FIELD.START_EXPIRE_ORDER,
              );
            }}
            oldStyleLabel={true}
            error={Boolean(
              startFieldError || dateRangeFieldError || startExpireFieldError,
            )}
            helperText={startFieldError}
          />

          <InputLabelDatePicker
            id="CalEndDate"
            label={languageContext("enddate")}
            name={languageContext("enddate")}
            value={activityData.endDate}
            disabled={disableForm}
            onChange={(event) => {
              updateactivityData({ endDate: event.target.value });
              clearActivityFieldErrorsByKeys(
                ACTIVITY_FIELD.END_DATE,
                ACTIVITY_FIELD.DATE_RANGE,
              );
            }}
            oldStyleLabel={true}
            error={Boolean(endFieldError || dateRangeFieldError)}
            helperText={endFieldError}
          />
        </Box>
        {dateRangeFieldError ? (
          <Box sx={{ width: "100%", mb: 1.25 }} role="alert">
            <Typography component="p" variant="body2" color="error">
              {dateRangeFieldError}
            </Typography>
          </Box>
        ) : null}

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-end"
          sx={{ gap: 2, mb: 1.25 }}
        >
          <HoursAndMinutesTime
            itemHours={activityData.hours}
            disabled={disableForm}
            setItemHours={(hours) => updateactivityData({ hours })}
            itemMinutes={activityData.minutes}
            setItemMinutes={(minutes) => updateactivityData({ minutes })}
            hourLabel={languageContext("hours")}
            minutesLabel={languageContext("minutes")}
          />
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              pl: { xs: 0, sm: 1 },
              width: "100px",
              flex: "0 0 100px",
              maxWidth: "100px",
              minWidth: "100px",
              margin: "8px 0",
            }}
          >
            <InputLabel htmlFor="addexternal-cost">
              {`${languageContext("cost")}:`}
            </InputLabel>
            <TextField
              id="addexternal-cost"
              fullWidth
              value={activityData.cost}
              disabled={disableForm}
              onChange={(e) => {
                updateactivityData({
                  cost: sanitizeCostInput(e.target.value),
                });
                clearActivityFieldErrorsByKeys(ACTIVITY_FIELD.COST);
              }}
              error={Boolean(costFieldError)}
              helperText={costFieldError}
              FormHelperTextProps={{
                ...activityFieldHelperTextProps,
                ...(costFieldError
                  ? { id: "addexternal-cost-helper-text" }
                  : {}),
              }}
              inputProps={{
                inputMode: "decimal",
                "aria-label": languageContext("cost"),
                maxLength: 20,
                "aria-invalid": costFieldError ? "true" : "false",
                "aria-describedby": costFieldError
                  ? "addexternal-cost-helper-text"
                  : undefined,
              }}
              sx={{
                maxWidth: "100px",
                "& fieldset": {
                  maxWidth: "95px",
                },
              }}
            />
          </Box>
        </Box>

        <ItemMetadataFields
          fields={itemMetadataFields}
          valuesById={itemMetadataValuesById}
          onChange={handleItemMetadataChange}
          disabled={disableForm}
          languageContext={languageContext}
          errorsByFieldId={itemMetadataErrorsById}
        />

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="SelPersonalGoal"
              variant="body1"
            >
              {languageContext("connecttogoal")}:
            </Typography>
            <PersonalGoalsDropdown
              ref={personalGoalDropdownRef}
              id="SelPersonalGoal"
              personalId={personalId}
              defaultValue={activity.personalGoalId || activity.goalId}
              updateParentState={updatePersonalGoalId}
              disabled={disableForm}
            />
          </Box>
        )}

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="TxtResponsible"
              variant="body1"
            >
              {languageContext("responsible1")}:
            </Typography>
            <TextField
              id="TxtResponsible"
              fullWidth
              disabled={disableForm}
              value={activityData.responsible}
              onChange={(e) =>
                updateactivityData({ responsible: e.target.value })
              }
            />
          </Box>
        )}

        {!fromEducationHistory && (
          <Box display="flex" flexWrap="wrap" mb={1.25} gap={2}>
            <Box flex={1} minWidth="200px">
              <Typography
                component="label"
                htmlFor="addactivity-link"
                variant="body1"
              >
                {languageContext("html-editor-link")}:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  id="addactivity-link"
                  disabled={disableForm}
                  value={activityData.link}
                  onChange={(e) => updateactivityData({ link: e.target.value })}
                  fullWidth
                />
                <IconButton
                  onClick={() =>
                    activityData.link && openExternalURL(activityData.link)
                  }
                  icon={<OpenInNewIcon />}
                  title={languageContext("openlink")}
                  disabled={!activityData.link || disableForm}
                />
              </Box>
            </Box>
            <Box flex={1} minWidth="200px">
              <Typography
                component="label"
                htmlFor="addactivity-link-label"
                variant="body1"
              >
                {languageContext("linklabel")}:
              </Typography>
              <TextField
                id="addactivity-link-label"
                disabled={disableForm}
                value={activityData.linkLabel}
                onChange={(e) =>
                  updateactivityData({ linkLabel: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>
        )}

        {allowAttachment && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="dropzoneInput"
              variant="body1"
            >
              {`${languageContext("document")} (${languageContext(
                "maxfilesize",
              ).toLowerCase()}: ${SettingStore.getSettingValue(
                "activityAttachmentMaxSize",
              )} MB):`}
            </Typography>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2">
                {languageContext("selectedfile")}:
              </Typography>
              {(activityData.files || activity.attachmentId) && (
                <>
                  <Box className="dropzone-selectedfiles">
                    {renderSelectedFiles()}
                  </Box>
                  {!activityData.removeAttachment && (
                    <IconButton
                      onClick={handleRemoveFile}
                      icon={<DeleteOutlined />}
                      title={languageContext("delete")}
                      size="small"
                    />
                  )}
                </>
              )}
            </Box>

            {uiState.fileError && (
              <Typography variant="body2" color="error" mb={1}>
                {uiState.fileError.replace(
                  "%maxfilesize%",
                  `${SettingStore.getSettingValue("activityAttachmentMaxSize")} MB`,
                )}
              </Typography>
            )}

            {fileExtensionFieldError ? (
              <Typography
                component="p"
                variant="body2"
                color="error"
                role="alert"
                mb={1}
              >
                {fileExtensionFieldError}
              </Typography>
            ) : null}

            <Dropzone
              onDrop={handleDrop}
              label={
                activityData.files || activity.attachmentId
                  ? languageContext("dropzoneinfo2")
                  : languageContext("dropzoneinfo")
              }
              allowedExtensions={uiState.allowedExtensions}
              disabled={disableForm}
            />
          </Box>
        )}

        <Box mb={1.25}>
          <Typography component="label" htmlFor="TxtComment" variant="body1">
            {languageContext("comment")}:
          </Typography>
          <TextFieldMultiline
            id="TxtComment"
            minRows={3}
            fullWidth
            disabled={disableForm}
            value={activityData.comment}
            onChange={(e) => updateactivityData({ comment: e.target.value })}
          />
        </Box>

        {showCompletedCheckbox && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <input
                type="checkbox"
                id="ChkCompleted"
                checked={activityData.isCompleted}
                disabled={activity.approved && activityData.isCompleted}
                onChange={(e) =>
                  updateactivityData({ isCompleted: e.target.checked })
                }
              />
              <Typography
                component="label"
                htmlFor="ChkCompleted"
                variant="body1"
                sx={{ cursor: "pointer" }}
              >
                {languageContext("coursecompleted")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            label={languageContext("cancel")}
          />
        </Grid>
        <Grid item>
          <Button
            type="submit"
            label={languageContext("save")}
            disabled={saveButtonDisabled}
          />
        </Grid>
        {!fromEducationHistory &&
          uiState.requireApprovalForCompletion &&
          !activity.approved &&
          activity.id &&
          !activityData.approvalRequested &&
          !isManager && (
            <Grid item>
              <Button
                color="secondary"
                variant="outlined"
                type="button"
                label={languageContext("requestapproval")}
                onClick={handleRequestApproval}
              />
            </Grid>
          )}
        {!fromEducationHistory &&
          uiState.requireApprovalForCompletion &&
          !activity.approved &&
          !activity.id &&
          !isManager && (
            <Grid item>
              <Button
                color="secondary"
                variant="outlined"
                type="button"
                label={languageContext("saveandrequestapproval")}
                onClick={() => handleSaveActivity(true)}
              />
            </Grid>
          )}
      </Grid>
      <SnackbarAlert
        open={uiState.success}
        onClose={() => updateUiState({ success: false })}
        message={languageContext("approvalrequested")}
        severity="success"
      />
      <SnackbarAlert
        open={uiState.error}
        onClose={() => updateUiState({ error: false })}
        message={languageContext("error")}
        severity="error"
      />
    </Form>
  );
};

export default withLanguageContext(AddExternal, "TalentManagement");
